import { useState } from 'react';
import { useDatos } from '../datos/DatosContext';
import { useAviso } from '../lib/avisos';
import { nuevoId } from '../lib/fechas';
import { Icono } from '../componentes/ui/Icono';
import { Cabecera, GrupoChips } from '../componentes/ui/basicos';
import type { NotaDestacada } from '../modelo/tipos';

const FILTROS = ['Activas', 'Fijadas', 'Inactivas', 'Archivadas', 'Todas'] as const;
type Filtro = (typeof FILTROS)[number];

const cumple: Record<Filtro, (n: NotaDestacada) => boolean> = {
  Activas: (n) => n.activa && !n.archivada,
  Fijadas: (n) => n.fijada && !n.archivada,
  Inactivas: (n) => !n.activa && !n.archivada,
  Archivadas: (n) => n.archivada,
  Todas: () => true
};

export function Notas() {
  const { datos, agregar, actualizar } = useDatos();
  const aviso = useAviso();
  const [nueva, setNueva] = useState('');
  const [tipo, setTipo] = useState(datos.opciones.tiposNota[0] ?? 'Afirmación');
  const [filtro, setFiltro] = useState<Filtro>('Activas');
  const [edicion, setEdicion] = useState<{ id: string; texto: string } | null>(null);

  const lista = datos.notas.filter(cumple[filtro]);

  const anadir = () => {
    const t = nueva.trim();
    if (!t) { aviso('Escribe la nota antes de añadirla.'); return; }
    agregar('notas', { id: nuevoId('n'), tipo, texto: t, activa: true, fijada: false, archivada: false });
    setNueva(''); setFiltro('Activas');
    aviso('Nota añadida (solo en esta sesión).');
  };

  const guardarEdicion = () => {
    if (!edicion) return;
    if (!edicion.texto.trim()) { aviso('La nota no puede quedar vacía.'); return; }
    actualizar('notas', edicion.id, { texto: edicion.texto.trim() });
    setEdicion(null);
    aviso('Nota actualizada.');
  };

  return (
    <div className="stack-lg">
      <Cabecera antetitulo="Volver a una frase" titulo="Notas destacadas" subtitulo="Funciona sin IA: crear, editar, fijar y archivar." />
      <div className="card stack-sm">
        <label className="lbl" htmlFor="nn">Nueva nota</label>
        <input id="nn" type="text" placeholder="Una afirmación, una cita, una intención…" value={nueva} onChange={(e) => setNueva(e.target.value)} />
        <GrupoChips etiqueta="Tipo de nota" opciones={datos.opciones.tiposNota} seleccion={tipo} onElegir={setTipo} />
        <div className="row" style={{ justifyContent: 'flex-end' }}><button className="btn primary" onClick={anadir}>Añadir nota</button></div>
      </div>

      <GrupoChips etiqueta="Filtrar notas" opciones={[...FILTROS]} seleccion={filtro} onElegir={(f) => { setFiltro(f as Filtro); setEdicion(null); }} desplazable />

      <div className="grid2">
        {lista.map((n) => {
          const editando = edicion?.id === n.id;
          return (
            <article key={n.id} className="card stack-sm">
              <div className="between">
                <span className="tag moss">{n.tipo}</span>
                <span className="row">
                  {n.fijada ? <span className="tag dusk"><Icono nombre="pin" tam="sm" />Fijada</span> : null}
                  {!n.activa ? <span className="tag">Inactiva</span> : null}
                </span>
              </div>
              {editando ? (
                <>
                  <label className="sr" htmlFor={'edit-' + n.id}>Editar nota</label>
                  <textarea id={'edit-' + n.id} rows={3} value={edicion.texto} onChange={(e) => setEdicion({ id: n.id, texto: e.target.value })} />
                  <div className="row"><button className="btn primary" onClick={guardarEdicion}>Guardar</button><button className="btn" onClick={() => setEdicion(null)}>Cancelar</button></div>
                </>
              ) : (
                <>
                  <p className="reading" style={{ fontSize: 20, lineHeight: 1.45, margin: '4px 0', fontStyle: 'italic' }}>{n.texto}</p>
                  <div className="row">
                    <button className="btn quiet small" onClick={() => setEdicion({ id: n.id, texto: n.texto })}>Editar</button>
                    <button className="btn quiet small" onClick={() => {
                      actualizar('notas', n.id, { fijada: !n.fijada, activa: n.fijada ? n.activa : true });
                      aviso(n.fijada ? 'Ya no aparece en el inicio.' : 'Fijada en la pantalla de inicio.');
                    }}>{n.fijada ? 'Desfijar' : 'Fijar en inicio'}</button>
                    <button className="btn quiet small" onClick={() => actualizar('notas', n.id, { activa: !n.activa, fijada: n.activa ? false : n.fijada })}>
                      {n.activa ? 'Desactivar' : 'Activar'}
                    </button>
                    <button className="btn quiet small" onClick={() => {
                      actualizar('notas', n.id, { archivada: !n.archivada, fijada: false });
                      aviso(n.archivada ? 'Nota restaurada.' : 'Archivada. No se ha borrado nada.');
                    }}><Icono nombre="archive" tam="sm" />{n.archivada ? 'Restaurar' : 'Archivar'}</button>
                  </div>
                </>
              )}
            </article>
          );
        })}
      </div>
      {!lista.length ? <p className="muted">No hay notas en este filtro.</p> : null}
      <p className="muted small" style={{ margin: 0 }}>Archivar nunca borra: puedes restaurar una nota desde «Archivadas».</p>
    </div>
  );
}
